/**
 * Shared localStorage helpers — single source of truth.
 *
 * Used by: remoteWorkReportService
 */

/**
 * Read & parse JSON from localStorage.
 * @param {string} key       localStorage key
 * @param {*}      fallback  returned on missing key or parse error (default [])
 */
export const readJSON = (key, fallback = []) => {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
    } catch {
        return fallback;
    }
};

/**
 * Stringify & write JSON to localStorage with QuotaExceededError handling.
 * @param {string} key
 * @param {*}      data
 * @param {{ trimKey?: string, trimMax?: number }} opts
 *   Optional auto-recovery: if QuotaExceededError, trim `trimKey` array to `trimMax` entries.
 */
export const writeJSON = (key, data, opts = {}) => {
    try {
        localStorage.setItem(key, JSON.stringify(data));
    } catch (e) {
        if (e?.name === "QuotaExceededError" || e?.code === 22) {
            if (opts.trimKey) {
                try {
                    const raw = localStorage.getItem(opts.trimKey);
                    if (raw) {
                        const arr = JSON.parse(raw);
                        if (arr.length > (opts.trimMax || 500)) {
                            localStorage.setItem(
                                opts.trimKey,
                                JSON.stringify(arr.slice(0, opts.trimMax || 500))
                            );
                            localStorage.setItem(key, JSON.stringify(data));
                            return;
                        }
                    }
                } catch { /* fallthrough */ }
            }
        }
        throw e;
    }
};

/**
 * Generate a prefixed unique ID.
 * @param {string} prefix  e.g. "hr", "dept", "et", "syg", "rwr"
 */
export const generateId = (prefix = "id") =>
    `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/** ISO timestamp shortcut */
export const now = () => new Date().toISOString();
